import { useCallback, useEffect, useRef, useState } from "preact/hooks";
import type { JSX } from "preact";
import { useI18n } from "../lib/i18n";

/**
 * The console's own "are you sure?" and "type it here".
 *
 * These replace the browser's confirm() and prompt(). Those boxes cannot be
 * translated or styled, say the page's address instead of the product's name,
 * can be switched off for a whole tab with one careless tick — after which
 * every delete silently does nothing — and block the thread that is supposed
 * to be delivering the next customer's message while they are open.
 *
 * A view asks with `await confirm(…)` / `await form(…)` and renders `dialog`
 * somewhere in its tree. One question at a time: a second ask while one is
 * open replaces it and answers the first as cancelled.
 */

export interface ConfirmOptions {
  title: string;
  body?: string;
  /** The word on the button that goes ahead. */
  confirmLabel?: string;
  /** Draws the go-ahead button as destructive. */
  danger?: boolean;
  /**
   * For the things with no undo: the button stays disabled until this exact
   * text has been typed, so a click meant for the row above deletes nothing.
   */
  typeToConfirm?: string;
}

export interface FormField {
  key: string;
  label: string;
  hint?: string;
  type?: "text" | "email" | "password";
  initial?: string;
  placeholder?: string;
  multiline?: boolean;
  /** Defaults to true. */
  required?: boolean;
  minLength?: number;
  maxLength?: number;
}

export interface FormOptions {
  title: string;
  body?: string;
  fields: FormField[];
  confirmLabel?: string;
  danger?: boolean;
}

type Request = { id: number } & (
  | { kind: "confirm"; options: ConfirmOptions; resolve: (value: boolean) => void }
  | { kind: "form"; options: FormOptions; resolve: (value: Record<string, string> | null) => void }
);

/** Distributes over the union, which a plain Omit would flatten into one shape. */
type NewRequest = Request extends infer R ? (R extends Request ? Omit<R, "id"> : never) : never;

export interface Dialogs {
  confirm: (options: ConfirmOptions) => Promise<boolean>;
  /** Resolves with the trimmed values by field key, or null when cancelled. */
  form: (options: FormOptions) => Promise<Record<string, string> | null>;
  /** One field; resolves with its trimmed value, or null when cancelled. */
  prompt: (options: Omit<FormOptions, "fields"> & Omit<FormField, "key">) => Promise<string | null>;
  dialog: JSX.Element | null;
}

function cancel(request: Request): void {
  if (request.kind === "confirm") request.resolve(false);
  else request.resolve(null);
}

export function useDialogs(): Dialogs {
  const [request, setRequest] = useState<Request | null>(null);
  const current = useRef<Request | null>(null);
  const seq = useRef(0);

  const open = useCallback((asked: NewRequest) => {
    if (current.current) cancel(current.current);
    const next = { ...asked, id: ++seq.current } as Request;
    current.current = next;
    setRequest(next);
  }, []);

  const close = useCallback(() => {
    current.current = null;
    setRequest(null);
  }, []);

  // A view that unmounts with a question open must not leave its caller waiting for ever.
  useEffect(
    () => () => {
      if (current.current) cancel(current.current);
    },
    [],
  );

  const confirm = useCallback(
    (options: ConfirmOptions) => new Promise<boolean>((resolve) => open({ kind: "confirm", options, resolve })),
    [open],
  );
  const form = useCallback(
    (options: FormOptions) =>
      new Promise<Record<string, string> | null>((resolve) => open({ kind: "form", options, resolve })),
    [open],
  );
  const prompt = useCallback<Dialogs["prompt"]>(
    async ({ title, body, confirmLabel, danger, ...field }) => {
      const values = await form({ title, body, confirmLabel, danger, fields: [{ key: "value", ...field }] });
      return values ? (values["value"] ?? "") : null;
    },
    [form],
  );

  const dialog = request ? <DialogView key={request.id} request={request} onClose={close} /> : null;
  return { confirm, form, prompt, dialog };
}

function DialogView({ request, onClose }: { request: Request; onClose: () => void }): JSX.Element {
  const { t } = useI18n();
  const { options } = request;
  const fields = request.kind === "form" ? request.options.fields : [];
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(fields.map((f) => [f.key, f.initial ?? ""])),
  );
  const [typed, setTyped] = useState("");
  const first = useRef<HTMLInputElement | HTMLTextAreaElement | HTMLButtonElement | null>(null);

  const dismiss = useCallback(() => {
    cancel(request);
    onClose();
  }, [request, onClose]);

  useEffect(() => {
    first.current?.focus();
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        event.stopPropagation();
        dismiss();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [dismiss]);

  const mustType = request.kind === "confirm" ? request.options.typeToConfirm : undefined;
  const ready =
    request.kind === "confirm"
      ? !mustType || typed.trim() === mustType.trim()
      : fields.every((f) => {
          const value = (values[f.key] ?? "").trim();
          if (!value) return f.required === false;
          return value.length >= (f.minLength ?? 0);
        });

  function submit(event: Event): void {
    event.preventDefault();
    if (!ready) return;
    if (request.kind === "confirm") request.resolve(true);
    else request.resolve(Object.fromEntries(fields.map((f) => [f.key, (values[f.key] ?? "").trim()])));
    onClose();
  }

  return (
    <div class="dialog-scrim" onClick={dismiss}>
      <form
        class="dialog"
        role={request.kind === "confirm" ? "alertdialog" : "dialog"}
        aria-modal="true"
        aria-labelledby="dialog-title"
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
      >
        <h2 id="dialog-title">{options.title}</h2>
        {options.body ? <p class="dialog-body">{options.body}</p> : null}

        {fields.map((field, index) => {
          const common = {
            value: values[field.key] ?? "",
            maxLength: field.maxLength,
            placeholder: field.placeholder,
            onInput: (e: JSX.TargetedEvent<HTMLInputElement | HTMLTextAreaElement>) =>
              setValues((v) => ({ ...v, [field.key]: e.currentTarget.value })),
          };
          return (
            <label key={field.key}>
              <span>
                {field.label} {field.hint ? <span class="hint">{field.hint}</span> : null}
              </span>
              {field.multiline ? (
                <textarea
                  class="plain"
                  rows={4}
                  ref={index === 0 ? (el) => void (first.current = el) : undefined}
                  {...common}
                  onKeyDown={(e) => {
                    // Enter adds a line in a textarea; Ctrl/⌘+Enter sends the form.
                    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submit(e);
                  }}
                />
              ) : (
                <input
                  type={field.type ?? "text"}
                  autocomplete={field.type === "password" ? "new-password" : "off"}
                  ref={index === 0 ? (el) => void (first.current = el) : undefined}
                  {...common}
                />
              )}
            </label>
          );
        })}

        {mustType ? (
          <label>
            <span>{t.common.typeToConfirm(mustType)}</span>
            <input
              type="text"
              autocomplete="off"
              value={typed}
              ref={(el) => void (first.current = el)}
              onInput={(e) => setTyped(e.currentTarget.value)}
            />
          </label>
        ) : null}

        <div class="dialog-actions">
          <button
            type="button"
            class="btn"
            onClick={dismiss}
            // A destructive question with nothing to type opens on "cancel": focus
            // has to land inside the dialog, and Enter must not be the way through.
            ref={fields.length === 0 && !mustType && options.danger ? (el) => void (first.current = el) : undefined}
          >
            {t.common.cancel}
          </button>
          <button
            type="submit"
            class={`btn primary${options.danger ? " danger" : ""}`}
            disabled={!ready}
            // With nothing to type, the go-ahead button takes focus.
            ref={fields.length === 0 && !mustType && !options.danger ? (el) => void (first.current = el) : undefined}
          >
            {options.confirmLabel ?? t.common.confirm}
          </button>
        </div>
      </form>
    </div>
  );
}
